<template>
	<core-page>
		<div class="omapi-personalization">
			<div class="omapi-personalization__filters">
				<div class="omapi-personalization__nav flex">
					<a
						v-for="category in categories"
						:key="category.slug"
						class="omapi-personalization__filter nohover"
						:class="{ 'omapi-personalization__filter-active': !search && filter === category.slug }"
						@click="updateFilter(category.slug)"
					>
						{{ category.displayName }}
					</a>
					<a
						class="omapi-personalization__filter nohover"
						:class="{ 'omapi-personalization__filter-active': allFilters }"
						@click="filter = ''"
						>All</a
					>
				</div>
				<div class="omapi-personalization__search campaign-type-filter__search">
					<input
						placeholder="Search..."
						type="text"
						class="omapi-input"
						:value="search"
						@input="updateSearch"
						@keydown.esc="clearSearch"
					/>
					<svg-times v-if="search" class="clear-search" @click="clearSearch" />
				</div>
			</div>

			<common-alerts id="om-plugin-alerts" :alerts="alerts" />

			<core-loading v-if="isLoading"><h1>Loading...</h1></core-loading>
			<personalization-rules
				v-else
				:categories="categories"
				:filter="filter"
				:search="search"
				@clearSearch="clearSearch"
			/>
		</div>
	</core-page>
</template>

<script>
import { mapActions } from 'vuex';

export default {
	data() {
		return {
			filter: 'popular',
			search: '',
			categories: [
				{
					slug: 'popular',
					displayName: 'Popular',
					order: 1,
				},
				{
					slug: 'timing',
					displayName: 'Trigger (When)',
					order: 2,
				},
				{
					slug: 'triggers',
					displayName: 'Targeting (Who)',
					order: 3,
				},
				{
					slug: 'behavior',
					displayName: 'Personalization',
					order: 4,
				},
				{
					slug: 'ecommerce',
					displayName: 'Ecommerce',
					order: 5,
				},
			],
		};
	},
	computed: {
		isLoading() {
			return !this.$store.getters.isFetched('rulesetData');
		},
		alerts() {
			const alerts = [...this.$get('$store.state.alerts', [])];
			return alerts.concat(this.$get('$store.state.campaigns.alerts', []));
		},
		allFilters() {
			return this.search || !this.filter;
		},
	},
	mounted() {
		if (this.$store.getters.shouldFetchUser) {
			this.$bus.$on('fetchedMe', this.fetchRulesetData);
			this.fetchMe();
		} else {
			this.fetchRulesetData();
		}
	},
	beforeDestroy() {
		this.$bus.$off('fetchedMe', this.fetchRulesetData);
	},
	methods: {
		...mapActions(['fetchMe']),
		...mapActions('campaigns', ['fetchRulesetData']),
		updateFilter(filter) {
			this.filter = filter;
			this.search = '';
		},
		clearSearch() {
			this.search = '';
		},
		updateSearch(e) {
			this.search = e.target.value;
		},
	},
};
</script>

<style lang="scss">
@import '../assets/scss/variables';
.omapi-screen .omapi-personalization {
	&__filters {
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-bottom: solid 2px $color_grey-cloud;
		margin: 8px 0 35px;
		@media screen and (max-width: 1139px) {
			flex-direction: column-reverse;
		}
	}
	&__nav {
		@media screen and (max-width: 1139px) {
			align-self: flex-start;
		}
	}
	&__filter {
		padding: 21px 18px 23px;
		font-size: 15px;
		line-height: 1.3em;
		color: $font_secondary-color;
		border-bottom: solid 2px $color_grey-cloud;
		margin-bottom: -2px;
		cursor: pointer;
		&-active {
			font-weight: $font_bold;
			color: $color_black;
			border-bottom-color: $color_blue-primary;
		}
		@media screen and (max-width: 999px) {
			padding: 17px 13px 19px;
		}
	}
	&__search {
		&.campaign-type-filter__search {
			svg {
				top: 9px;
			}
		}
		@media screen and (max-width: 1139px) {
			align-self: flex-end;
		}
	}
	&__category {
		border-bottom: solid 1px $color_grey-separator;
		margin-bottom: 30px;
		padding-bottom: 30px;
		&-title {
			font-size: 16px;
			line-height: 1.4em;
			margin: 0 0 30px;
			display: flex;
			align-items: center;
			> svg {
				margin-right: 5px;
				height: 20px;
			}
		}
		&-search {
			border-bottom: solid 1px $color_grey-separator;
			padding-bottom: 24px;
			margin-bottom: 26px;

			&-icon {
				vertical-align: middle;
				margin-left: 8px;
				cursor: pointer;
				line-height: 1;

				svg {
					background: rgb(209, 215, 222);
					color: #fff;
					width: 9px;
					height: 9px;
					border-radius: 100%;
					padding: 3px;
				}
			}
			> svg {
				height: 14px;
				margin-right: 8px;
			}
		}
		&:last-child {
			border-bottom: none;
			margin-bottom: 7px;
			padding-bottom: 0;
		}
	}
	&__rules {
		display: flex;
		flex-wrap: wrap;
	}
	&__rule {
		flex-basis: 30.5%;
		margin-bottom: 20px;
		padding-right: 21px;
		position: relative;
		&-name {
			margin: 0 0 2px;
			line-height: 1.3em;
			font-size: 14px;
			display: inline-flex;
			align-items: center;
			svg {
				height: 14px;
				color: #b5bfc9;
				margin-left: 5px;
			}

			&:hover {
				cursor: pointer;
				.omapi-personalization__rule-create {
					visibility: visible;
					// We only want a delay before hiding the overlay, not on showing it
					transition-delay: 0s;
				}
				.omapi-personalization__rule-name svg {
					color: #087ce1;
				}
			}
		}
		&-desc {
			color: $font_setting-color;
			line-height: 1.4em;
			font-size: 14px;
		}
		&-create {
			visibility: hidden;
			position: absolute;
			width: 128px;
			text-align: center;
			padding: 20px 20px 15px;
			z-index: 111;
			top: 25px;
			left: -7px;
			box-shadow: 0px 3px 15px rgb(0 0 0 / 10%);
			border: 1px solid #c9d0d6;
			border-radius: 3px;
			transition-duration: 0s;
			transition-property: visibility;
			transition-delay: 0.2s;

			&:before {
				content: '';
				background: #fff;
				border-radius: 3px;
				height: 100%;
				width: 100%;
				position: absolute;
				top: 0;
				left: 0;
			}

			&:after {
				content: '';
				width: 10px;
				background: #fff;
				top: -5px;
				left: 13px;
				height: 10px;
				position: absolute;
				transform: rotate(45deg);
				z-index: -1;
				border: 1px solid #c9d0d6;
				border-radius: 2px;
			}

			.omapi-button.omapi-button__small {
				margin: 0 0 10px;
				padding: 4px 6px 5px;
				width: 114px;
				position: relative;
			}
			.omapi-link-arrow-after {
				margin-right: 0;
				padding-right: 0;
				font-weight: normal;
				font-size: 12px;
				line-height: 1;
				text-decoration-line: underline;
				&:after {
					content: ' →';
					margin-left: 0;
				}
			}
		}
		&-add {
			position: relative;
			display: inherit;
		}
		@media screen and (max-width: 1199px) {
			flex-basis: 47%;
		}
		@media screen and (max-width: 999px) {
			flex-basis: 100%;
			padding-right: 0;
		}
	}
}
</style>
